// src/portal-app/nav/routes.js
//
// Pushed-screen route registry (W2 Task 2) — the single source of truth for
// which pushable screens are BUILT. A route id is any screen id: for hub
// destinations it matches the card id in the nav map (src/portal-app/nav/
// tabs.js) — a card id absent from ROUTES is not yet built, HubScreen keeps
// it inert (Task 4) and NavStack ignores any push() for it — but sub-screens
// with no hub card (e.g. an employee detail pushed from a list) register here
// under their own id just the same.
//
// Entry shape (one entry per screen — later tasks append, never restructure):
//
//   routeId: {
//     component:     React component rendered by NavStackHost. Receives the
//                    push() params object spread as props, plus the RESOLVED
//                    entry meta as `route` — so `route` is a reserved name
//                    push params must not use, and screens never import
//                    ROUTES to find their own title (cycle + drift).
//     titleKey:      translation key for the pushed screen's nav-bar title —
//                    a string, or a function of the push params for dynamic
//                    titles: (params) => \`employee_${params.id}\`.
//     titleFallback: English fallback for titleKey (t(key, {}, fallback)) —
//                    string or function of params, same as titleKey.
//   }
//
// Later waves add operations / team / more destinations (and their
// sub-screens) the same way. Plain data + component references only — no
// side effects at module load.

import AccountScreen from '../more/AccountScreen';
import DevicesScreen from '../more/DevicesScreen';
import HelpScreen from '../more/HelpScreen';
import InventoryScreen from '../more/InventoryScreen';
import MarketingScreen from '../more/MarketingScreen';
import ProductsScreen from '../more/ProductsScreen';
import SettingsScreen from '../more/SettingsScreen';
import EmployeesScreen from '../team/EmployeesScreen';
import RolesScreen from '../team/RolesScreen';
import EmployeeDetailScreen from '../team/EmployeeDetailScreen';
import ScheduleScreen from '../team/ScheduleScreen';
import PayRunsScreen from '../team/PayRunsScreen';
import PayRunDetailScreen from '../team/PayRunDetailScreen';
import FinancialPerformanceScreen from '../insights/FinancialPerformanceScreen';
import ProductIntelligenceScreen from '../insights/ProductIntelligenceScreen';
import ProductDetailScreen from '../insights/ProductDetailScreen';
import CostManagementScreen from '../insights/CostManagementScreen';
import OperationalEfficiencyScreen from '../insights/OperationalEfficiencyScreen';
import TransactionsScreen from '../operations/TransactionsScreen';
import EodScreen from '../operations/EodScreen';
import ExpensesScreen from '../operations/ExpensesScreen';
import ReservationsScreen from '../operations/ReservationsScreen';

export const ROUTES = {
    // W2 Task 6 — Financial Performance (Insights hub card 'financial').
    financial: {
        component: FinancialPerformanceScreen,
        titleKey: 'hub_financial_performance',
        titleFallback: 'Financial Performance',
    },
    // W2 Task 7 — Product Intelligence (Insights hub card 'product').
    product: {
        component: ProductIntelligenceScreen,
        titleKey: 'hub_product_intelligence',
        titleFallback: 'Product Intelligence',
    },
    // W2 Task 7 — Product Detail: a sub-screen with NO hub card, pushed by
    // the list via push('product-detail', { code, name, range }). The nav
    // bar title is the PRODUCT NAME — deliberately registered with NO
    // titleKey: t() does an exact-string lookup, so a raw entity name used
    // as a KEY would collide with system UI keys (a product literally named
    // "Total" or "Delivery" would render that key's translation instead of
    // its own name). NavStack renders `titleFallback` directly when
    // `titleKey` is absent. A param-less deep entry degrades to the generic
    // "Product Detail". Wave 4's Employee Detail must copy THIS shape.
    'product-detail': {
        component: ProductDetailScreen,
        titleFallback: (params) => (params && params.name) || 'Product Detail',
    },
    // W2 Task 8 — Cost Management (Insights hub card 'cost').
    cost: {
        component: CostManagementScreen,
        titleKey: 'hub_cost_management',
        titleFallback: 'Cost Management',
    },
    // W2 Task 9 — Operational Efficiency (Insights hub card 'operational').
    operational: {
        component: OperationalEfficiencyScreen,
        titleKey: 'hub_operational_efficiency',
        titleFallback: 'Operational Efficiency',
    },
    // W3 Task 2 — Transactions + Voids (Operations hub card 'transactions').
    transactions: {
        component: TransactionsScreen,
        titleKey: 'hub_transactions',
        titleFallback: 'Transactions',
    },
    // W3 Task 3 — End of Day (Operations hub card 'eod').
    eod: {
        component: EodScreen,
        titleKey: 'hub_end_of_day',
        titleFallback: 'End of Day',
    },
    // W3 Task 4 — Expenses (Operations hub card 'expenses').
    expenses: {
        component: ExpensesScreen,
        titleKey: 'hub_expenses',
        titleFallback: 'Expenses',
    },
    // W3 Task 5 — Reservations (Operations hub card 'reservations').
    reservations: {
        component: ReservationsScreen,
        titleKey: 'hub_reservations',
        titleFallback: 'Reservations',
    },
    // W4 Task 1 — Employees (Team hub card 'employees').
    employees: {
        component: EmployeesScreen,
        titleKey: 'hub_employees',
        titleFallback: 'Employees',
    },
    // W4 Task 4 — Schedule (Team hub card 'schedule').
    schedule: {
        component: ScheduleScreen,
        titleKey: 'hub_schedule',
        titleFallback: 'Schedule',
    },
    // W4 Task 2 — Employee Detail: pushed sub-screen with NO hub card.
    // titleFallback is a function: the entity name (never a t() key — see
    // product-detail comment above for the full rationale).
    'employee-detail': {
        component: EmployeeDetailScreen,
        titleFallback: (params) => (params && params.name) || 'Employee Detail',
    },
    // W4 Task 5 — Pay Runs list (Team hub card 'payruns').
    payruns: {
        component: PayRunsScreen,
        titleKey: 'hub_pay_runs',
        titleFallback: 'Pay Runs',
    },
    // W4 Task 5 — Pay Run detail: pushed sub-screen. titleFallback is a
    // function: renders the period string as the nav-bar title, same pattern
    // as product-detail and employee-detail.
    'payrun-detail': {
        component: PayRunDetailScreen,
        titleFallback: (params) => (params && params.period) || 'Pay Run',
    },
    // W4 Task 6 — Roles + Users (Team hub card 'roles').
    roles: {
        component: RolesScreen,
        titleKey: 'hub_roles',
        titleFallback: 'Roles',
    },
    // W5 Task 1 — Devices (More hub card 'devices').
    devices: {
        component: DevicesScreen,
        titleKey: 'hub_devices',
        titleFallback: 'Devices',
    },
    // W5 Task 2 — Products (More hub card 'products').
    products: {
        component: ProductsScreen,
        titleKey: 'hub_products',
        titleFallback: 'Products',
    },
    // W5 Task 3 — Inventory (More hub card 'inventory').
    inventory: {
        component: InventoryScreen,
        titleKey: 'hub_inventory',
        titleFallback: 'Inventory',
    },
    // W5 Task 4 — Settings (More hub card 'settings').
    settings: {
        component: SettingsScreen,
        titleKey: 'hub_settings',
        titleFallback: 'Settings',
    },
    // W5 Task 5 — Marketing (More hub card 'marketing').
    marketing: {
        component: MarketingScreen,
        titleKey: 'hub_marketing',
        titleFallback: 'Marketing',
    },
    // W5 Task 6 — Help (More hub card 'help').
    help: {
        component: HelpScreen,
        titleKey: 'hub_help',
        titleFallback: 'Help',
    },
    // W5 Task 6 — Account (More hub card 'account').
    account: {
        component: AccountScreen,
        titleKey: 'hub_account',
        titleFallback: 'Account',
    },
};
